import { COLORS, PRIMARY_SCALE, withAlpha } from '@/constants/colors'

// Plain (non-hook) theme colour table. Kept separate from the hook so the MUI
// theme can be built from it without pulling in react-redux.

export interface ThemeColors {
  // Backgrounds
  bgChrome: string // app frame behind the sidebar and the page sheet
  bgBase: string // page background (the rounded content sheet)
  bgSurface: string // cards, panels
  bgPanel: string // sidebar, ai panel (slightly different from surface)
  bgElevated: string // elevated cards / hover states
  bgGlass: string // glass card background
  bgInput: string // input field background
  bgHover: string // generic hover background

  // Borders
  border: string // standard border
  borderSubtle: string // very subtle dividers
  borderFocus: string // focus ring (primary)

  // Text
  textPrimary: string
  textSecondary: string
  textMuted: string

  // Primary accent, tuned per mode so it stays readable as text/icon colour
  primaryText: string // links, active labels, icons on surfaces
  primarySoft: string // selected / active background tint
  primaryBorder: string // selected / active border

  // Status text, tuned per mode for contrast on tinted chips
  successText: string
  warningText: string
  errorText: string

  // Elevation
  shadowSm: string
  shadowMd: string

  // Navbar
  navbarBg: string

  // Toast
  toastBg: string

  // Mode flag
  isDark: boolean
}

function buildThemeColors(isDark: boolean): ThemeColors {
  if (isDark) {
    return {
      bgChrome: '#05070c',
      bgBase: '#0b0f19',
      bgSurface: '#111827',
      bgPanel: '#0d1117',
      bgElevated: '#1a2235',
      bgGlass: 'rgba(17,24,39,0.72)',
      bgInput: 'rgba(255,255,255,0.04)',
      bgHover: 'rgba(255,255,255,0.05)',
      border: 'rgba(255,255,255,0.08)',
      borderSubtle: 'rgba(255,255,255,0.06)',
      borderFocus: withAlpha(COLORS.PRIMARY, 0.6),
      textPrimary: '#f1f5f9',
      textSecondary: '#94a3b8',
      textMuted: '#7c8aa0',
      primaryText: PRIMARY_SCALE[400],
      primarySoft: withAlpha(COLORS.PRIMARY, 0.16),
      primaryBorder: withAlpha(COLORS.PRIMARY, 0.45),
      successText: '#34d399',
      warningText: '#fbbf24',
      errorText: '#f87171',
      shadowSm: '0 1px 2px rgba(0,0,0,0.4)',
      shadowMd: '0 8px 24px rgba(0,0,0,0.45)',
      navbarBg: 'rgba(11,15,25,0.85)',
      toastBg: 'rgba(17,24,39,0.96)',
      isDark: true,
    }
  }

  return {
    bgChrome: '#eceff4',
    bgBase: '#f8fafc',
    bgSurface: '#ffffff',
    bgPanel: '#ffffff',
    bgElevated: '#eef1f6',
    bgGlass: '#ffffff',
    bgInput: '#f8fafc',
    bgHover: 'rgba(15,23,42,0.04)',
    border: 'rgba(15,23,42,0.10)',
    borderSubtle: 'rgba(15,23,42,0.07)',
    borderFocus: withAlpha(COLORS.PRIMARY, 0.6),
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    primaryText: PRIMARY_SCALE[700],
    primarySoft: withAlpha(COLORS.PRIMARY, 0.08),
    primaryBorder: withAlpha(COLORS.PRIMARY, 0.35),
    successText: '#047857',
    warningText: '#b45309',
    errorText: '#b91c1c',
    shadowSm: '0 1px 2px rgba(15,23,42,0.06)',
    shadowMd: '0 8px 24px rgba(15,23,42,0.08)',
    navbarBg: 'rgba(255,255,255,0.9)',
    toastBg: 'rgba(255,255,255,0.98)',
    isDark: false,
  }
}

// Built once per mode and shared, so components get the same object on every
// render. Style hooks key their memo on it, and a fresh object each time made
// every re-render recompile every component's CSS. Treat as read-only.
const DARK_COLORS = Object.freeze(buildThemeColors(true))
const LIGHT_COLORS = Object.freeze(buildThemeColors(false))

export function getThemeColors(isDark: boolean): ThemeColors {
  return isDark ? DARK_COLORS : LIGHT_COLORS
}
